import React, { useEffect, useState } from "react";
import API_URL from "../../services/api";
import "./Profile.css";

const Profile = () => {
  const [user] = useState(() =>
    JSON.parse(localStorage.getItem("user"))
  );

  const [form, setForm] = useState({
    name: "",
    email: "",
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) {
      setForm({
        name: user.name || "",
        email: user.email || "",
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
    }
  }, [user]);

  // ============================
  // LOGIN CHECK
  // ============================

  if (!user || !user.user_id) {
    return (
      <div className="profile-login-message">
        <div className="profile-login-card">
          <div className="login-icon">🔐</div>

          <h2>Login Required</h2>

          <p>
            Your login session could not be found.
            Please login again.
          </p>
        </div>
      </div>
    );
  }

  // ============================
  // HANDLE INPUT
  // ============================

  const handleChange = (e) => {
    setForm({
      ...form,
      [e.target.name]: e.target.value,
    });
  };

  // ============================
  // UPDATE PROFILE
  // ============================

  const updateProfile = async () => {
    if (!form.name.trim() || !form.email.trim()) {
      alert("Please fill all required fields.");
      return;
    }

    if (
      form.newPassword &&
      !form.currentPassword
    ) {
      alert("Please enter your current password.");
      return;
    }

    if (
      form.newPassword &&
      form.newPassword !== form.confirmPassword
    ) {
      alert("New passwords do not match.");
      return;
    }

    if (
      form.newPassword &&
      form.newPassword.length < 8
    ) {
      alert(
        "New password must be at least 8 characters long."
      );
      return;
    }

    setLoading(true);

    try {
      const res = await fetch(
        `${API_URL}/api/user/profile/${user.user_id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(form),
        }
      );

      const text = await res.text();

      let data;

      try {
        data = JSON.parse(text);
      } catch {
        data = {
          message: "Invalid server response.",
        };
      }

      if (!res.ok) {
        alert(
          data.message || "Failed to update profile."
        );
        setLoading(false);
        return;
      }

      // Update local storage
      const updatedUser = {
        ...user,
        name: form.name,
        email: form.email,
      };

      localStorage.setItem(
        "user",
        JSON.stringify(updatedUser)
      );

      // Clear password fields
      setForm({
        ...form,
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });

      alert("Profile updated successfully.");

      setLoading(false);
    } catch (err) {
      console.log("PROFILE UPDATE ERROR:", err);

      alert(
        "Server error. Please try again."
      );

      setLoading(false);
    }
  };

  return (
    <div className="profile-page">

      <div className="profile-card">

        {/* ============================
            HEADER
        ============================ */}

        <div className="profile-header">

          <div className="profile-avatar">
            👤
          </div>

          <div className="profile-header-text">

            <h1>My Profile</h1>

            <p>
              Manage your account information
            </p>

          </div>

        </div>

        <div className="profile-divider"></div>

        {/* ============================
            ACCOUNT SUMMARY
        ============================ */}

        <div className="account-summary">

          <div className="summary-item">

            <span className="summary-label">
              Account Name
            </span>

            <strong>
              {form.name || "Not available"}
            </strong>

          </div>

          <div className="summary-item">

            <span className="summary-label">
              Account Role
            </span>

            <strong className="role-badge">
              {user.role || "User"}
            </strong>

          </div>

        </div>

        {/* ============================
            PERSONAL INFORMATION
        ============================ */}

        <div className="profile-section">

          <div className="section-header">

            <div className="section-icon">
              👤
            </div>

            <div>
              <h2>Personal Information</h2>

              <p>
                Update your basic account information.
              </p>
            </div>

          </div>

          <div className="profile-form">

            <div className="form-group">

              <label>
                Full Name
              </label>

              <input
                type="text"
                name="name"
                value={form.name}
                onChange={handleChange}
                placeholder="Enter your name"
              />

            </div>

            <div className="form-group">

              <label>
                Email Address
              </label>

              <input
                type="email"
                name="email"
                value={form.email}
                onChange={handleChange}
                placeholder="Enter your email"
              />

            </div>

          </div>

        </div>

        {/* ============================
            PASSWORD SECTION
        ============================ */}

        <div className="profile-section password-section">

          <div className="section-header">

            <div className="section-icon">
              🔒
            </div>

            <div>
              <h2>Change Password</h2>

              <p>
                Leave the password fields empty if
                you do not want to change it.
              </p>
            </div>

          </div>

          <div className="profile-form password-form">

            <div className="form-group">

              <label>
                Current Password
              </label>

              <input
                type={
                  showPassword
                    ? "text"
                    : "password"
                }
                name="currentPassword"
                value={form.currentPassword}
                onChange={handleChange}
                placeholder="Enter current password"
              />

            </div>

            <div className="form-group">

              <label>
                New Password
              </label>

              <input
                type={
                  showPassword
                    ? "text"
                    : "password"
                }
                name="newPassword"
                value={form.newPassword}
                onChange={handleChange}
                placeholder="Enter new password"
              />

            </div>

            <div className="form-group">

              <label>
                Confirm New Password
              </label>

              <input
                type={
                  showPassword
                    ? "text"
                    : "password"
                }
                name="confirmPassword"
                value={form.confirmPassword}
                onChange={handleChange}
                placeholder="Confirm new password"
              />

            </div>

          </div>

          <label className="show-password">

            <input
              type="checkbox"
              checked={showPassword}
              onChange={(e) =>
                setShowPassword(
                  e.target.checked
                )
              }
            />

            <span>
              Show Passwords
            </span>

          </label>

        </div>

        {/* ============================
            SAVE BUTTON
        ============================ */}

        <div className="profile-actions">

          <button
            className="save-profile-btn"
            onClick={updateProfile}
            disabled={loading}
          >
            {loading
              ? "Saving Changes..."
              : "Save Changes"}
          </button>

        </div>

      </div>

    </div>
  );
};

export default Profile;